import { useState, useEffect } from "react";

// View state that makes a link shareable, kept in the URL hash after the tab: #statistics?view=compare&a=...
// It is written with replaceState, so it neither adds history entries nor fires hashchange.

const hashParts = () => {
  const hash = window.location.hash.replace(/^#/, "");
  const at = hash.indexOf("?");
  return at < 0 ? [hash, ""] : [hash.slice(0, at), hash.slice(at + 1)];
};

// The tab name in the hash, without its parameters
export const hashTabName = () => hashParts()[0].toLowerCase();

export const readHashParam = (key) => new URLSearchParams(hashParts()[1]).get(key);

// A value that is empty or equal to the default is left out, which keeps the URL short
export const writeHashParam = (key, value, defaultValue) => {
  const [name, query] = hashParts();
  const params = new URLSearchParams(query);
  if (value === null || value === undefined || value === "" || String(value) === String(defaultValue)) {
    params.delete(key);
  } else {
    params.set(key, String(value));
  }
  const next = params.toString();
  try {
    window.history.replaceState(null, "", `#${name}${next ? `?${next}` : ""}`);
  } catch (e) {
    // the link just stays as it was
  }
};

// useState that starts from the URL and follows the state back into it.
// parse turns the raw URL text into a value (undefined if it is not valid, which falls back to the default);
// serialize turns the value into URL text. The parameter is removed again when the component unmounts.
export const useHashParam = (key, defaultValue, { parse = (raw) => raw, serialize = String } = {}) => {
  const [value, setValue] = useState(() => {
    const raw = readHashParam(key);
    const parsed = raw === null ? undefined : parse(raw);
    return parsed === undefined ? defaultValue : parsed;
  });
  const text = serialize(value);
  const defaultText = serialize(defaultValue);
  useEffect(() => {
    writeHashParam(key, text, defaultText);
    return () => writeHashParam(key, null); // a screen that is gone leaves nothing in the link
  }, [key, text, defaultText]);
  return [value, setValue];
};

// parse helper: one of a fixed set of words
export const oneOf = (allowed) => (raw) => (allowed.includes(raw) ? raw : undefined);

// parse helper: a whole number
export const asInteger = (raw) => (/^\d+$/.test(raw) ? Number(raw) : undefined);
